import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Button,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  KeyboardCommandKey as CommandIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';

interface HeaderProps {
  height: number;
  onToggleSidebar: () => void;
  onOpenCommandPalette: () => void;
}

export function Header({ height, onToggleSidebar, onOpenCommandPalette }: HeaderProps) {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        height,
      }}
    >
      <Toolbar sx={{ height, minHeight: height }}>
        {/* Menu Toggle */}
        <IconButton
          onClick={onToggleSidebar}
          sx={{ mr: 2, color: 'text.secondary' }}
        >
          <MenuIcon />
        </IconButton>

        {/* Search Button */}
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={onOpenCommandPalette}
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            borderRadius: 2,
            px: 2,
            py: 0.75,
            minWidth: 200,
            justifyContent: 'flex-start',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha('#1976d2', 0.04),
            },
          }}
        >
          <Box sx={{ flexGrow: 1, textAlign: 'left' }}>Search...</Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              ml: 2,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              bgcolor: 'action.hover',
              fontSize: '0.75rem',
            }}
          >
            <CommandIcon sx={{ fontSize: 14 }} />
            <span>K</span>
          </Box>
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton sx={{ color: 'text.secondary', mr: 1 }}>
            <NotificationsIcon />
          </IconButton>
        </Tooltip>

        {/* User Menu */}
        <Box
          onClick={handleMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            p: 0.5,
            borderRadius: 2,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Avatar
            src={user?.avatarUrl}
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
            }}
          >
            {user ? getInitials(user.fullName) : '?'}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
              {user?.fullName || 'User'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', lineHeight: 1.2 }}
            >
              {user?.roles?.[0] || 'Employee'}
            </Typography>
          </Box>
        </Box>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 200,
              mt: 1,
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2">{user?.fullName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
          <MenuItem>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
